import type { Warehouse } from '../models/warehouse';
import type { StockBalance } from '../models/stockBalance';
import { computeSellableQuantity } from '../models/stockBalance';

export type StockDemandLine = {
  id?: string;
  productId?: string;
  selectedQuantity?: number;
  quantity?: number;
  warehouseId?: string;
  name?: string;
  code?: string;
};

export function catalogProductIdFromLine(line: {
  productId?: string;
  id?: string;
}): string {
  return String(line.productId || line.id || '').trim();
}

export function resolveLineWarehouseId(
  line: { warehouseId?: string },
  headerWarehouseId?: string,
  defaultWarehouseId?: string
): string {
  return String(
    line.warehouseId || headerWarehouseId || defaultWarehouseId || ''
  ).trim();
}

export function sellableAtWarehouse(
  balances: StockBalance[] | undefined,
  warehouseId: string | undefined,
  /** Units this pending sale already holds at the warehouse (do not treat as unavailable). */
  creditReservedQty: number = 0
): number {
  const whId = String(warehouseId || '').trim();
  if (!whId) return 0;
  const row = (balances || []).find(
    balance =>
      balance.warehouseId === whId && balance.active !== false
  );
  if (!row) return Math.max(0, creditReservedQty);
  const free = Math.max(
    0,
    computeSellableQuantity(row.onHand ?? 0, row.reserved ?? 0)
  );
  return free + Math.max(0, creditReservedQty);
}

/** Sellable for one form line after siblings in the same bucket take their share. */
export function lineSellableWithSharedPool(
  balances: StockBalance[] | undefined,
  warehouseId: string | undefined,
  creditReservedQty: number,
  siblingDemandQty: number
): number {
  const pool = sellableAtWarehouse(balances, warehouseId, creditReservedQty);
  return Math.max(0, pool - Math.max(0, siblingDemandQty));
}

export function siblingDemandAtWarehouse(
  lines: StockDemandLine[],
  excludeLineId: string | undefined,
  productId: string,
  warehouseId: string,
  headerWarehouseId?: string,
  defaultWarehouseId?: string
): number {
  const pid = String(productId || '').trim();
  const whId = String(warehouseId || '').trim();
  if (!pid || !whId) return 0;
  let total = 0;
  for (const line of lines) {
    if (excludeLineId && String(line.id || '') === excludeLineId) continue;
    if (catalogProductIdFromLine(line) !== pid) continue;
    const lineWh = resolveLineWarehouseId(
      line,
      headerWarehouseId,
      defaultWarehouseId
    );
    if (lineWh !== whId) continue;
    const qty = Number(line.selectedQuantity ?? line.quantity);
    if (Number.isFinite(qty) && qty > 0) total += qty;
  }
  return total;
}

export function totalSellableAcrossWarehouses(
  balances: StockBalance[] | undefined,
  /** Optional per-warehouse credits for this pending sale's existing holds. */
  creditByWarehouseId?: Record<string, number>
): number {
  const warehouseIds = new Set<string>();
  for (const row of balances || []) {
    const whId = String(row.warehouseId || '').trim();
    if (whId) warehouseIds.add(whId);
  }
  for (const whId of Object.keys(creditByWarehouseId || {})) {
    if (whId) warehouseIds.add(whId);
  }
  let sum = 0;
  for (const whId of Array.from(warehouseIds)) {
    sum += sellableAtWarehouse(
      balances,
      whId,
      creditByWarehouseId?.[whId] ?? 0
    );
  }
  return sum;
}

export function resolveWarehouseWithSufficientSellable(
  balances: StockBalance[] | undefined,
  quantity: number,
  preferredWarehouseId: string,
  warehouses: Warehouse[],
  /** Credit only applies to the preferred warehouse (this sale's current hold). */
  creditReservedAtPreferred: number = 0
): string {
  const qty = Number.isFinite(quantity) ? quantity : 0;
  const preferred = String(preferredWarehouseId || '').trim();
  if (!preferred || qty <= 0) return preferred;

  if (
    sellableAtWarehouse(balances, preferred, creditReservedAtPreferred) >= qty
  ) {
    return preferred;
  }

  const ordered = [...warehouses].sort((a, b) =>
    String(a.name || a.id || '').localeCompare(String(b.name || b.id || ''))
  );
  for (const warehouse of ordered) {
    const whId = String(warehouse.id || '').trim();
    if (!whId || whId === preferred) continue;
    if (sellableAtWarehouse(balances, whId) >= qty) {
      return whId;
    }
  }

  return preferred;
}

export function formatWarehouseStockHint(
  balances: StockBalance[] | undefined,
  warehouses: Warehouse[],
  preferredWarehouseId?: string
): string {
  const warehouseNameById = new Map(
    warehouses.map(warehouse => [
      warehouse.id,
      warehouse.name || warehouse.code || warehouse.id,
    ])
  );
  const parts = (balances || [])
    .map(row => {
      if (row.active === false) return null;
      const sellable = Math.max(
        0,
        computeSellableQuantity(row.onHand ?? 0, row.reserved ?? 0)
      );
      if (sellable <= 0) return null;
      const label =
        warehouseNameById.get(row.warehouseId) || row.warehouseId;
      const suffix =
        row.warehouseId === preferredWarehouseId ? ' (seleccionado)' : '';
      return `${label}: ${sellable}${suffix}`;
    })
    .filter((value): value is string => Boolean(value));

  return parts.length > 0 ? parts.join(' · ') : 'Sin stock en almacenes activos';
}

export function hasInsufficientStockAtWarehouse(
  balances: StockBalance[] | undefined,
  warehouseId: string | undefined,
  quantity: number,
  creditReservedQty: number = 0
): boolean {
  if (balances === undefined) return false;
  const qty = Number.isFinite(quantity) ? quantity : 0;
  if (qty <= 0) return false;
  return sellableAtWarehouse(balances, warehouseId, creditReservedQty) < qty;
}

export function hasSufficientStockSomewhere(
  balances: StockBalance[] | undefined,
  quantity: number,
  creditByWarehouseId?: Record<string, number>
): boolean {
  if (balances === undefined) return false;
  const qty = Number.isFinite(quantity) ? quantity : 0;
  if (qty <= 0) return true;
  return totalSellableAcrossWarehouses(balances, creditByWarehouseId) >= qty;
}

export function isProductStockBalancesLoaded(
  balances: StockBalance[] | undefined
): boolean {
  return balances !== undefined;
}

/** Sum original pending-sale holds for a product at one warehouse (or header fallback). */
export function reservedQtyHeldByOrderLines(
  lines:
    | Array<{ productId?: string; quantity?: number; warehouseId?: string }>
    | undefined,
  productId: string,
  warehouseId: string | undefined,
  headerWarehouseId?: string | undefined
): number {
  const pid = String(productId || '').trim();
  const whId = String(warehouseId || '').trim();
  if (!pid || !whId) return 0;
  const header = String(headerWarehouseId || '').trim();
  let total = 0;
  for (const line of lines || []) {
    if (String(line.productId || '').trim() !== pid) continue;
    const lineWh = String(line.warehouseId || header || '').trim();
    if (lineWh !== whId) continue;
    const qty = Number(line.quantity);
    if (Number.isFinite(qty) && qty > 0) total += qty;
  }
  return total;
}

export type AggregatedStockGateResult =
  | { ok: true }
  | { ok: false; reason: 'balances_unloaded'; productId: string }
  | {
      ok: false;
      reason: 'missing_warehouse' | 'insufficient';
      line: StockDemandLine;
      productId: string;
      warehouseId: string;
    };

/**
 * Fail-closed multi-warehouse persist gate: require loaded balances, aggregate
 * demand per (productId, warehouseId), and apply pending hold credit once.
 */
export function evaluateAggregatedStockGate(args: {
  lines: StockDemandLine[];
  balancesByProductId: Record<string, StockBalance[] | undefined>;
  holdLines?:
    | Array<{ productId?: string; quantity?: number; warehouseId?: string }>
    | undefined;
  holdHeaderWarehouseId?: string;
  headerWarehouseId?: string;
  defaultWarehouseId?: string;
}): AggregatedStockGateResult {
  const {
    lines,
    balancesByProductId,
    holdLines,
    holdHeaderWarehouseId,
    headerWarehouseId,
    defaultWarehouseId,
  } = args;

  type Bucket = {
    productId: string;
    warehouseId: string;
    demand: number;
    sampleLine: StockDemandLine;
  };
  const buckets = new Map<string, Bucket>();

  for (const line of lines) {
    const productId = catalogProductIdFromLine(line);
    if (!productId) continue;
    const warehouseId = resolveLineWarehouseId(
      line,
      headerWarehouseId,
      defaultWarehouseId
    );
    const qty = Number(line.selectedQuantity ?? line.quantity);
    if (!Number.isFinite(qty) || qty <= 0) continue;

    if (!warehouseId) {
      return {
        ok: false,
        reason: 'missing_warehouse',
        line,
        productId,
        warehouseId: '',
      };
    }

    if (!isProductStockBalancesLoaded(balancesByProductId[productId])) {
      return { ok: false, reason: 'balances_unloaded', productId };
    }

    const key = `${productId}::${warehouseId}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.demand += qty;
    } else {
      buckets.set(key, {
        productId,
        warehouseId,
        demand: qty,
        sampleLine: line,
      });
    }
  }

  for (const bucket of Array.from(buckets.values())) {
    const credit = reservedQtyHeldByOrderLines(
      holdLines,
      bucket.productId,
      bucket.warehouseId,
      holdHeaderWarehouseId
    );
    if (
      hasInsufficientStockAtWarehouse(
        balancesByProductId[bucket.productId],
        bucket.warehouseId,
        bucket.demand,
        credit
      )
    ) {
      return {
        ok: false,
        reason: 'insufficient',
        line: bucket.sampleLine,
        productId: bucket.productId,
        warehouseId: bucket.warehouseId,
      };
    }
  }

  return { ok: true };
}
